// Globals

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Button, DatePicker, Select } from '@/ui';
import { FormItem, IFormInstance } from '@/components';
import { StyledPageWrapper, StyledForm, StyledWarning } from './ProductOfMonthFormStyles';

// Models
import { IProduct } from '@/models/products.model';
import { IPartnerProfile } from '@/models/partners.model';

// Modules
import { getPartnersOptions } from '@/modules/Partners';

// Helpers
import {
  getActiveOptions,
  ProductsOfMonthFields,
  normalizeProductValues,
  buildProductOptions,
  getProductList,
  TGetProductsOfMonthForm,
} from '../../helpers';
import { useProductFormValidationRules } from '../../hooks';

type TProductOfMonthForm = {
  formInstance: IFormInstance;
  initialValues: IProduct | null;
  debouncedFetchProducts: (searchText: string) => void;
  productsList?: IProduct[];
  partnerList?: IPartnerProfile[];
  searchPartners: (search?: string) => void;
  handleSubmit: (values: TGetProductsOfMonthForm) => Promise<void>;
  isEdit: boolean;
  isLoading: boolean;
};

const ProductOfMonthForm = ({
  formInstance,
  initialValues,
  debouncedFetchProducts,
  productsList,
  partnerList,
  searchPartners,
  handleSubmit,
  isEdit,
  isLoading,
}: TProductOfMonthForm) => {
  const { t } = useTranslation(['products']);
  const rules = useProductFormValidationRules();
  const editOptions = buildProductOptions({
    options: getProductList(productsList),
    label: `${initialValues?.sku} (${initialValues?.name})`,
    value: initialValues?.id ?? '',
  });
  const options = isEdit ? editOptions : getProductList(productsList);

  const handleSearch = (value: string) => {
    debouncedFetchProducts(value);
  };

  useEffect(() => {
    if (initialValues) {
      formInstance.setFieldsValue(normalizeProductValues(initialValues));
    }
  }, [initialValues, formInstance]);

  return (
    <StyledPageWrapper>
      <StyledWarning>{t('products_of_month_notification_info')}</StyledWarning>
      <StyledForm form={formInstance} onFinish={handleSubmit}>
        {!isEdit && (
          <FormItem name={ProductsOfMonthFields.PARTNER_ID} label={t('products_list_partner')}>
            <Select
              onSearch={searchPartners}
              options={getPartnersOptions(partnerList ?? [])}
              showSearch
              allowClear
              disabled={isEdit}
            />
          </FormItem>
        )}

        <FormItem
          name={ProductsOfMonthFields.PRODUCT_ID}
          label={t('products_list_product')}
          extra={t('products_article_search')}
          rules={rules[ProductsOfMonthFields.PRODUCT_ID]}
        >
          <Select options={options} onSearch={handleSearch} showSearch allowClear disabled={isEdit} />
        </FormItem>

        <FormItem
          name={ProductsOfMonthFields.PUBLISHED}
          label={t('products_list_status')}
          rules={rules[ProductsOfMonthFields.PUBLISHED]}
        >
          <Select options={getActiveOptions(t)} />
        </FormItem>

        <FormItem
          name={ProductsOfMonthFields.DATE_FROM}
          label={t('products_list_start')}
          rules={rules[ProductsOfMonthFields.DATE_FROM]}
        >
          <DatePicker />
        </FormItem>

        <FormItem
          name={ProductsOfMonthFields.DATE_TO}
          label={t('products_list_end')}
          rules={rules[ProductsOfMonthFields.DATE_TO]}
        >
          <DatePicker />
        </FormItem>

        <Button type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
          {isEdit ? t('products_list_update') : t('products_list_save')}
        </Button>
      </StyledForm>
    </StyledPageWrapper>
  );
};

export default ProductOfMonthForm;
